import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { storage } from '../utils/storageManager';
import { TrendingUp, Clock, BookOpen, Target, Download, Sparkles, Calendar, Award, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Growth Dashboard
 * 
 * Practical insights and analytics for spiritual practice
 * - Meditation statistics and trends
 * - Journal activity
 * - Habit tracking progress
 * - Practice streaks
 * - Data export functionality
 */

interface DashboardStats {
  meditation: {
    totalSessions: number;
    totalMinutes: number;
    streak: number;
    favoriteType: string;
    weeklyAverage: number;
  };
  journal: {
    totalEntries: number;
    thisWeek: number;
    mostUsedMood: string;
  };
  habits: {
    totalHabits: number;
    completedToday: number;
    bestStreak: number;
  };
}

const GrowthDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    meditation: {
      totalSessions: 0,
      totalMinutes: 0,
      streak: 0,
      favoriteType: 'mindfulness',
      weeklyAverage: 0
    },
    journal: {
      totalEntries: 0,
      thisWeek: 0,
      mostUsedMood: 'reflective'
    },
    habits: {
      totalHabits: 0,
      completedToday: 0,
      bestStreak: 0
    }
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // Load meditation stats
      const meditationStats = await storage.getMeditationStats();

      // Load journal stats
      const journalEntries = await storage.getJournalEntries(1000);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const thisWeekEntries = journalEntries.filter(e => new Date(e.createdAt) >= oneWeekAgo);
      
      const moodCounts = journalEntries.reduce((acc, entry) => {
        if (entry.mood) {
          acc[entry.mood] = (acc[entry.mood] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      const mostUsedMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'reflective';

      // Load habit stats
      const habits = await storage.getHabits();
      const today = new Date().toISOString().split('T')[0];
      const completedToday = habits.filter(h => h.completions.includes(today)).length;
      const bestStreak = Math.max(...habits.map(h => h.streak), 0);

      setStats({
        meditation: meditationStats,
        journal: {
          totalEntries: journalEntries.length,
          thisWeek: thisWeekEntries.length,
          mostUsedMood
        },
        habits: {
          totalHabits: habits.length,
          completedToday,
          bestStreak
        }
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      await storage.downloadExport();
      toast.success('✨ Data exported successfully!');
    } catch (error) {
      console.error('Failed to export data:', error);
      toast.error('Failed to export data');
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#64748b' }}>
        <Sparkles size={60} style={{ margin: '0 auto 1rem', opacity: 0.5, animation: 'spin 3s linear infinite' }} />
        <p>Loading your spiritual journey...</p>
      </div>
    );
  }

  const statCards = [
    {
      icon: Clock,
      title: 'Total Meditation Time',
      value: `${Math.floor(stats.meditation.totalMinutes / 60)}h ${stats.meditation.totalMinutes % 60}m`,
      subtitle: `${stats.meditation.totalSessions} sessions`,
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6, #6366f1)'
    },
    {
      icon: Target,
      title: 'Meditation Streak',
      value: `${stats.meditation.streak} days`,
      subtitle: `${stats.meditation.weeklyAverage} sessions this week`,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981, #059669)'
    },
    {
      icon: BookOpen,
      title: 'Journal Entries',
      value: stats.journal.totalEntries,
      subtitle: `${stats.journal.thisWeek} this week`,
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)'
    },
    {
      icon: Award,
      title: 'Best Habit Streak',
      value: `${stats.habits.bestStreak} days`,
      subtitle: `${stats.habits.completedToday}/${stats.habits.totalHabits} done today`,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}
      >
        <div>
          <h2
            style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#8b5cf6',
              marginBottom: '0.5rem'
            }}
          >
            <BarChart3
              size={28}
              style={{
                display: 'inline',
                marginRight: '0.5rem',
                verticalAlign: 'middle'
              }}
            />
            Growth Dashboard
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Track your spiritual practice and personal development
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              style={{
                background: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: `1px solid ${card.color}40`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: card.gradient,
                opacity: 0.1,
                borderRadius: '50%',
                transform: 'translate(30%, -30%)'
              }} />

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  padding: '10px',
                  background: `${card.color}20`,
                  borderRadius: '8px',
                  display: 'flex'
                }}>
                  <Icon size={24} color={card.color} />
                </div>
                <h3 style={{ color: '#f1f5f9', fontSize: '0.875rem', fontWeight: '600' }}>
                  {card.title}
                </h3>
              </div>

              <div style={{ fontSize: '2rem', fontWeight: '700', color: card.color, marginBottom: '0.5rem' }}>
                {card.value}
              </div>

              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {card.subtitle}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Insights */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1.5rem' }}>
        {/* Meditation Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="fr00t-card"
          style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderColor: 'rgba(139, 92, 246, 0.3)'
          }}
        >
          <h3 style={{ color: '#8b5cf6', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={20} />
            Meditation Insights
          </h3>

          <div style={{ fontSize: '0.875rem', color: '#f1f5f9', lineHeight: '1.6' }}>
            {stats.meditation.totalSessions > 0 ? (
              <>
                <p style={{ marginBottom: '0.75rem' }}>
                  You've completed <strong style={{ color: '#8b5cf6' }}>{stats.meditation.totalSessions} meditation sessions</strong>, spending {Math.floor(stats.meditation.totalMinutes / 60)} hours in mindful practice.
                </p>
                
                {stats.meditation.streak > 0 && (
                  <p style={{ marginBottom: '0.75rem' }}>
                    🔥 Your current streak of <strong style={{ color: '#10b981' }}>{stats.meditation.streak} days</strong> shows excellent consistency!
                  </p>
                )}

                <p style={{ marginBottom: '0.75rem' }}>
                  Your favorite practice is <strong style={{ color: '#6366f1' }}>{stats.meditation.favoriteType}</strong> meditation.
                </p>

                {stats.meditation.weeklyAverage >= 5 ? (
                  <p style={{ color: '#10b981' }}>
                    ⭐ Excellent! You're averaging {stats.meditation.weeklyAverage} sessions per week.
                  </p>
                ) : stats.meditation.weeklyAverage >= 3 ? (
                  <p style={{ color: '#6366f1' }}>
                    👍 Good progress with {stats.meditation.weeklyAverage} sessions this week.
                  </p>
                ) : (
                  <p style={{ color: '#64748b' }}>
                    💡 Try to meditate a few more times this week for better benefits.
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: '#64748b' }}>
                Start your meditation journey to see insights here.
              </p>
            )}
          </div>
        </motion.div>

        {/* Journal Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="fr00t-card"
          style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderColor: 'rgba(99, 102, 241, 0.3)'
          }}
        >
          <h3 style={{ color: '#6366f1', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BookOpen size={20} />
            Journal Insights
          </h3>

          <div style={{ fontSize: '0.875rem', color: '#f1f5f9', lineHeight: '1.6' }}>
            {stats.journal.totalEntries > 0 ? (
              <>
                <p style={{ marginBottom: '0.75rem' }}>
                  You've written <strong style={{ color: '#6366f1' }}>{stats.journal.totalEntries} journal entries</strong>, documenting your spiritual journey.
                </p>

                <p style={{ marginBottom: '0.75rem' }}>
                  This week you added <strong style={{ color: '#8b5cf6' }}>{stats.journal.thisWeek} new entries</strong>.
                </p>

                <p style={{ marginBottom: '0.75rem' }}>
                  Your most common mood is <strong style={{ color: '#f59e0b' }}>{stats.journal.mostUsedMood}</strong>.
                </p>

                {stats.journal.thisWeek === 0 ? (
                  <p style={{ color: '#64748b' }}>
                    💭 Consider writing a reflection this week.
                  </p>
                ) : (
                  <p style={{ color: '#10b981' }}>
                    ✨ Great work staying connected to your inner experience!
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: '#64748b' }}>
                Start journaling to track your thoughts and insights.
              </p>
            )}
          </div>
        </motion.div>

        {/* Habits Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="fr00t-card"
          style={{
            background: 'rgba(30, 41, 59, 0.8)',
            borderColor: 'rgba(245, 158, 11, 0.3)'
          }}
        >
          <h3 style={{ color: '#f59e0b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Target size={20} />
            Habit Tracking
          </h3>

          <div style={{ fontSize: '0.875rem', color: '#f1f5f9', lineHeight: '1.6' }}>
            {stats.habits.totalHabits > 0 ? (
              <>
                <p style={{ marginBottom: '0.75rem' }}>
                  You're tracking <strong style={{ color: '#f59e0b' }}>{stats.habits.totalHabits} habits</strong> for personal growth.
                </p>

                <p style={{ marginBottom: '0.75rem' }}>
                  Today you've completed <strong style={{ color: '#10b981' }}>{stats.habits.completedToday} out of {stats.habits.totalHabits}</strong> habits.
                </p>

                {stats.habits.bestStreak > 0 && (
                  <p style={{ marginBottom: '0.75rem' }}>
                    Your best streak is <strong style={{ color: '#8b5cf6' }}>{stats.habits.bestStreak} days</strong>! Keep it up!
                  </p>
                )}

                {stats.habits.completedToday === stats.habits.totalHabits ? (
                  <p style={{ color: '#10b981' }}>
                    🎉 Perfect day! All habits completed!
                  </p>
                ) : stats.habits.completedToday > 0 ? (
                  <p style={{ color: '#6366f1' }}>
                    👏 Good progress! Keep going with the remaining habits.
                  </p>
                ) : (
                  <p style={{ color: '#64748b' }}>
                    🌅 Start your day by completing your first habit.
                  </p>
                )}
              </>
            ) : (
              <p style={{ color: '#64748b' }}>
                Create habits to track your daily spiritual practices.
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Sacred Numerology Display */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="fr00t-card"
        style={{
          marginTop: '2rem',
          textAlign: 'center',
          background: 'rgba(139, 92, 246, 0.1)',
          borderColor: 'rgba(139, 92, 246, 0.3)'
        }}
      >
        <h3 style={{ color: '#8b5cf6', marginBottom: '0.75rem' }}>
          Your Spiritual Journey by the Numbers
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '1rem',
          marginTop: '1rem'
        }}>
          <div>
            <div style={{ fontSize: '2rem', color: '#8b5cf6', marginBottom: '0.25rem' }}>
              {stats.meditation.totalSessions}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Sessions</div>
          </div>
          <div>
            <div style={{ fontSize: '2rem', color: '#6366f1', marginBottom: '0.25rem' }}>
              {stats.journal.totalEntries}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Entries</div>
          </div>
          <div>
            <div style={{ fontSize: '2rem', color: '#10b981', marginBottom: '0.25rem' }}>
              {stats.meditation.streak}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Day Streak</div>
          </div>
          <div>
            <div style={{ fontSize: '2rem', color: '#f59e0b', marginBottom: '0.25rem' }}>
              {stats.habits.bestStreak}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Best Streak</div>
          </div>
        </div>

        <div style={{
          marginTop: '1.5rem',
          fontSize: '0.75rem',
          color: '#64748b',
          fontStyle: 'italic'
        }}>
          "What we measure, we improve. Track your journey with intention." - Fr00t Wisdom
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GrowthDashboard;

